/**
 * Targets — shared spatial index for hittable/interactable entities.
 *
 * Used on both client and server to efficiently query nearby entities
 * for weapon targeting, projectile collision, and interact detection.
 *
 * Entries store a point (x, y) and a radius representing the entity's
 * target circle. Queries return all entries whose circle overlaps the
 * query circle: dist(entry, query) <= queryRadius + entry.radius.
 */

const CELL_SIZE = 2 // world units per grid cell

export class Targets {
  constructor() {
    this._cells = new Map()
  }

  /**
   * Register an entity. ox/oy offset the center from the base position.
   * Returns a handle with setPosition(px, py) and destroy().
   */
  add({ px, py, ox = 0, oy = 0, radius = 0, value }) {
    const entry = {
      x: px + ox,
      y: py + oy,
      radius,
      value,
      _ox: ox,
      _oy: oy,
      _cx: 0,
      _cy: 0,
    }
    this._insertEntry(entry)

    const self = this
    return {
      setPosition(px, py) {
        const nx = px + entry._ox
        const ny = py + entry._oy
        const ncx = self._cell(nx)
        const ncy = self._cell(ny)
        if (ncx !== entry._cx || ncy !== entry._cy) {
          self._removeEntry(entry)
          self._addToCell(entry, ncx, ncy)
        }
        entry.x = nx
        entry.y = ny
      },
      destroy() {
        self._removeEntry(entry)
      },
    }
  }

  /**
   * Return all entries whose circle overlaps the query circle.
   * i.e. dist(entry.center, (x, y)) <= radius + entry.radius
   */
  query(x, y, radius) {
    const results = []
    const r = radius + CELL_SIZE // extra cell padding covers max entity radius
    const minCx = this._cell(x - r)
    const maxCx = this._cell(x + r)
    const minCy = this._cell(y - r)
    const maxCy = this._cell(y + r)
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const cell = this._cells.get(this._key(cx, cy))
        if (!cell) continue
        for (const e of cell) {
          const dx = e.x - x
          const dy = e.y - y
          const threshold = radius + e.radius
          if (dx * dx + dy * dy <= threshold * threshold) {
            results.push(e)
          }
        }
      }
    }
    return results
  }

  _cell(v) {
    return Math.floor(v / CELL_SIZE)
  }

  _key(cx, cy) {
    return `${cx},${cy}`
  }

  _insertEntry(entry) {
    const cx = this._cell(entry.x)
    const cy = this._cell(entry.y)
    this._addToCell(entry, cx, cy)
  }

  _addToCell(entry, cx, cy) {
    entry._cx = cx
    entry._cy = cy
    const key = this._key(cx, cy)
    let cell = this._cells.get(key)
    if (!cell) {
      cell = []
      this._cells.set(key, cell)
    }
    cell.push(entry)
  }

  _removeEntry(entry) {
    const key = this._key(entry._cx, entry._cy)
    const cell = this._cells.get(key)
    if (!cell) return
    const i = cell.indexOf(entry)
    if (i !== -1) cell.splice(i, 1)
    if (!cell.length) this._cells.delete(key)
  }
}
